import {
  sessionEngagementKindSchema,
  sessionLocationKindSchema,
} from "@/modules/classes/domain/contracts/sessions";
import { z } from "zod";
import { offSiteDistanceSchema } from "../lib/off-site-distance";
import { privateLessonPackageKindSchema } from "./templates";

const bookingLocationFields = {
  locationKind: sessionLocationKindSchema
    .nullish()
    .transform((v) => v ?? "studio"),
  offSiteLabel: z.union([z.string(), z.null()]).optional(),
  offSiteDistance: offSiteDistanceSchema.nullish(),
  engagementKind: sessionEngagementKindSchema
    .nullish()
    .transform((v) => v ?? "standard"),
  roomId: z.string().optional(),
};

function refineBookingOffSiteDistance(
  data: {
    locationKind: "studio" | "off_site";
    offSiteDistance?: "near" | "far" | null;
    roomId?: string;
  },
  ctx: z.RefinementCtx,
) {
  if (data.locationKind === "studio" && data.offSiteDistance != null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "ສະຕູດິໂອບໍ່ໃຊ້ໄລຍະນອກສະຖານທີ່",
      path: ["offSiteDistance"],
    });
  }
  if (data.locationKind === "off_site" && data.offSiteDistance == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "ຕ້ອງເລືອກໄລຍະນອກສະຖານທີ່ (ໃກ້/ໄກ)",
      path: ["offSiteDistance"],
    });
  }
}

/**
 * Schema สำหรับ Create Booking
 */
export const CreateBookingSchema = z
  .object({
    packageId: z.string().min(1, "ຕ້ອງເລືອກ package").optional(),
    /** Used when packageId is omitted — server picks FIFO active package */
    studentId: z.string().min(1).optional(),
    date: z.string().min(1, "ຕ້ອງເລືອກວັນທີ"),
    startTime: z.string().min(1, "ຕ້ອງເລືອກເວລາເລີ່ມຕົ້ນ"),
    endTime: z.string().min(1, "ຕ້ອງເລືອກເວລາສິ້ນສຸດ"),
    instructorIds: z.array(z.string()).min(1, "ຕ້ອງເລືອກຢ່າງນ້ອຍ 1 ຄູ"),
    notes: z.string().optional(),
    ...bookingLocationFields,
  })
  .refine((data) => !!data.packageId || !!data.studentId, {
    message: "ຕ້ອງລະບຸ package ຫຼື ນັກຮຽນ",
    path: ["studentId"],
  })
  .refine(
    (data) =>
      data.locationKind !== "studio" ||
      (data.roomId != null && data.roomId.trim().length > 0),
    {
      message: "ຕ້ອງເລືອກຫ້ອງ",
      path: ["roomId"],
    },
  )
  .refine(
    (data) =>
      data.locationKind !== "off_site" ||
      data.roomId == null ||
      data.roomId.trim().length === 0,
    {
      message: "ນອກສະຖານທີ່ບໍ່ໃຊ້ຫ້ອງສະຕູດິໂອ",
      path: ["roomId"],
    },
  )
  .superRefine(refineBookingOffSiteDistance);

export type CreateBookingDTO = z.infer<typeof CreateBookingSchema>;

/**
 * Schema สำหรับ Update Booking
 */
export const UpdateBookingSchema = z
  .object({
    date: z.string().min(1, "ຕ້ອງເລືອກວັນທີ"),
    startTime: z.string().min(1, "ຕ້ອງເລືອກເວລາເລີ່ມຕົ້ນ"),
    endTime: z.string().min(1, "ຕ້ອງເລືອກເວລາສິ້ນສຸດ"),
    instructorIds: z.array(z.string()).min(1, "ຕ້ອງເລືອກຢ່າງນ້ອຍ 1 ຄູ"),
    notes: z.string().optional(),
    /** ເຫດຜົນການປ່ຽນແປງ (ຕ້ອງກຳນົດເມື່ອມີການປ່ຽນແປງເວລາ/ຫ້ອງ/ຄູ) */
    changeReason: z.string().min(1).optional(),
    ...bookingLocationFields,
  })
  .refine(
    (data) =>
      data.locationKind !== "studio" ||
      (data.roomId != null && data.roomId.trim().length > 0),
    {
      message: "ຕ້ອງເລືອກຫ້ອງ",
      path: ["roomId"],
    },
  )
  .refine(
    (data) =>
      data.locationKind !== "off_site" ||
      data.roomId == null ||
      data.roomId.trim().length === 0,
    {
      message: "ນອກສະຖານທີ່ບໍ່ໃຊ້ຫ້ອງສະຕູດິໂອ",
      path: ["roomId"],
    },
  )
  .superRefine(refineBookingOffSiteDistance);

export type UpdateBookingDTO = z.infer<typeof UpdateBookingSchema>;

/**
 * Booking DTO สำหรับ response
 */
export const BookingDTOSchema = z.object({
  id: z.string(),
  packageId: z.string(),
  date: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  durationMinutes: z.number(),
  locationKind: sessionLocationKindSchema,
  offSiteLabel: z.string().nullable(),
  offSiteDistance: offSiteDistanceSchema.nullable(),
  engagementKind: sessionEngagementKindSchema,
  roomId: z.string().nullable(),
  roomName: z.string(),
  instructorIds: z.array(z.string()),
  instructorNames: z.array(z.string()),
  status: z.enum(["scheduled", "completed", "cancelled"]),
  notes: z.string().nullable(),
  studentId: z.string(),
  studentName: z.string(),
  studentMemberId: z.string(),
  packageTemplateName: z.string(),
  packageKind: privateLessonPackageKindSchema.default("individual"),
  groupSize: z.number().nullable().optional(),
  memberAttendanceCount: z.number().optional(),
  packageRemainingHours: z.number(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type BookingDTO = z.infer<typeof BookingDTOSchema>;

/**
 * Booking Detail DTO (สำหรับ detail page)
 */
export const PackageMemberDTOSchema = z.object({
  studentId: z.string(),
  studentName: z.string(),
  studentMemberId: z.string(),
});

export const BookingAttendanceRecordDTOSchema = z.object({
  id: z.string(),
  studentId: z.string(),
  studentName: z.string(),
  studentMemberId: z.string(),
  checkedInAt: z.string(),
  checkedInBy: z.string(),
  overrideReason: z.string().nullable(),
  overrideBy: z.string().nullable(),
  createdAt: z.string(),
});

export const BookingDetailDTOSchema = BookingDTOSchema.extend({
  package: z.object({
    id: z.string(),
    templateName: z.string(),
    packageKind: privateLessonPackageKindSchema,
    groupSize: z.number().nullable(),
    totalHours: z.number(),
    remainingHours: z.number(),
    startDate: z.string(),
    expireDate: z.string(),
    status: z.string(),
  }),
  members: z.array(PackageMemberDTOSchema).optional(),
  attendances: z.array(BookingAttendanceRecordDTOSchema).optional(),
});

export type BookingDetailDTO = z.infer<typeof BookingDetailDTOSchema>;

/**
 * Booking DTO for calendar view (minimal fields)
 */
export const BookingCalendarDTOSchema = z.object({
  id: z.string(),
  date: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  locationKind: sessionLocationKindSchema,
  offSiteLabel: z.string().nullable(),
  offSiteDistance: offSiteDistanceSchema.nullable(),
  engagementKind: sessionEngagementKindSchema,
  roomId: z.string().nullable(),
  roomName: z.string(),
  instructorIds: z.array(z.string()),
  instructorNames: z.array(z.string()),
  status: z.enum(["scheduled", "completed", "cancelled"]),
  studentName: z.string(),
  packageKind: privateLessonPackageKindSchema.optional(),
  groupSize: z.number().nullable().optional(),
});

export type BookingCalendarDTO = z.infer<typeof BookingCalendarDTOSchema>;
